import { NpcNoyauPill } from '@/components/admin/npc/npc-card';
import {
  type BandChip,
  type BandSegment,
  type CurrentExpected,
  type ExperienceBand,
  experienceBands,
} from '@/lib/npc-expected-synthesis';
import { NOYAU_DEEP_CLASS, noyauPillInkClass } from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import { type NpcReplayRow, isNoyauCode, noyauOf } from '@matter_extractor/shared';

/**
 * The band of synthesis of the tab « Rejeux » (npc.md §26.4, planche 3 `Rejeux.dc.html`; issue #216,
 * lot L): one line PER EXPERIENCE played, under the reminder of the expected noyau of the current
 * version of the NPC. Every version of an experience counts together, and nothing is ever summed
 * across two experiences (A10); an NPC whose expected noyau changed between its versions has the
 * line of an experience cut by expected noyau (H36).
 *
 * The figures are counts of the rows on screen (`experienceBands`, pure): the verdict of each replay
 * was computed by the server and is only counted here. Told in words first - « PSG × 3 », « Niveaux
 * tenus : Pourquoi 0/3 » - the pills repeat it in colour.
 */

export interface NpcReplaysSynthesisProps {
  readonly rows: readonly NpcReplayRow[];
  /** The current version of the NPC and its expected noyau, for the reminder; null when unknown. */
  readonly current: CurrentExpected | null;
}

export function NpcReplaysSynthesis({ rows, current }: NpcReplaysSynthesisProps) {
  const bands = experienceBands(rows);
  return (
    <section
      data-testid="npc-rejeux-synthese"
      aria-labelledby="npc-rejeux-synthese-titre"
      className="space-y-1 rounded-md bg-gray-50 px-5 py-4"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pb-2">
        <h3 id="npc-rejeux-synthese-titre" className="font-semibold text-body text-gray-900">
          Face à l’attendu
        </h3>
        {current === null ? null : <CurrentReminder current={current} />}
      </div>
      {bands.map((band) => (
        <Band key={band.experienceId} band={band} />
      ))}
    </section>
  );
}

function CurrentReminder({ current }: { current: CurrentExpected }) {
  const { expected, versionNumber } = current;
  return (
    <span
      data-testid="npc-rejeux-synthese-attendu"
      className="inline-flex flex-wrap items-center gap-1.5 text-gray-700 text-small"
    >
      {expected === null ? (
        `pas de noyau attendu sur la fiche (v${versionNumber})`
      ) : isNoyauCode(expected) ? (
        <>
          attendu de la v{versionNumber} <NpcNoyauPill expected={expected} size="caption" />{' '}
          {noyauOf(expected).name}
        </>
      ) : (
        `attendu de la v${versionNumber} : hors base`
      )}
    </span>
  );
}

function Band({ band }: { band: ExperienceBand }) {
  return (
    <div
      data-testid="npc-rejeux-bande"
      data-experience={band.experienceId}
      className="grid gap-x-4 gap-y-2 border-gray-200 border-t py-3 lg:grid-cols-[14rem_minmax(0,1fr)]"
    >
      <p className="text-small">
        <span data-testid="npc-rejeux-bande-nom" className="font-semibold">
          {band.experienceName}
        </span>
        <span data-testid="npc-rejeux-bande-compte" className="text-gray-700">
          {' '}
          · {band.countLabel}
        </span>
      </p>
      <div className="space-y-3">
        {band.segments.map((segment, index) => (
          <Segment key={segment.heading ?? `segment-${index}`} segment={segment} />
        ))}
      </div>
    </div>
  );
}

function Segment({ segment }: { segment: BandSegment }) {
  const facts = [segment.levels, segment.placement, segment.remark].filter(
    (part): part is string => part !== null,
  );
  return (
    <div
      data-testid="npc-rejeux-segment"
      data-expected={segment.expected ?? 'none'}
      className="space-y-2"
    >
      {segment.heading === null ? null : (
        <p data-testid="npc-rejeux-segment-titre" className="font-semibold text-small">
          {segment.heading}
        </p>
      )}
      <div className="grid gap-x-4 gap-y-2 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <ul className="flex flex-wrap items-start gap-2">
          {segment.chips.map((chip) => (
            <li key={chip.label}>
              <Chip chip={chip} />
            </li>
          ))}
        </ul>
        {facts.length === 0 ? null : (
          <div className="space-y-1 text-gray-700 text-small leading-6">
            {segment.levels === null ? null : (
              <p data-testid="npc-rejeux-niveaux">{segment.levels}</p>
            )}
            {segment.placement === null ? null : (
              <p data-testid="npc-rejeux-rang">{segment.placement}</p>
            )}
            {segment.remark === null ? null : (
              <p data-testid="npc-rejeux-remarque">{segment.remark}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const CHIP = 'inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold text-small';

function Chip({ chip }: { chip: BandChip }) {
  const marks = {
    'data-testid': 'npc-rejeux-puce',
    'data-kind': chip.kind,
    'data-noyau': chip.noyau ?? undefined,
    'data-count': chip.count,
  } as const;
  if (chip.kind === 'found' && chip.noyau !== null) {
    return (
      <span
        {...marks}
        data-expected={chip.expected ? 'true' : undefined}
        className={cn(
          CHIP,
          noyauPillInkClass(chip.noyau),
          chip.expected && 'outline-dashed outline-2 outline-bleu-nuit-deep outline-offset-2',
        )}
        style={{ backgroundColor: noyauOf(chip.noyau).color }}
      >
        {chip.label}
      </span>
    );
  }
  if (chip.kind === 'expected' && chip.noyau !== null) {
    return (
      <span
        {...marks}
        data-expected="true"
        className={cn(CHIP, 'border-2 border-dashed bg-white', NOYAU_DEEP_CLASS[chip.noyau])}
      >
        {chip.label}
      </span>
    );
  }
  if (chip.kind === 'no-found') {
    return (
      <span {...marks} className={cn(CHIP, 'border border-gray-400 bg-white text-gray-700')}>
        {chip.label}
      </span>
    );
  }
  return (
    <span {...marks} className={cn(CHIP, 'bg-gray-100 text-gray-700')}>
      {chip.label}
    </span>
  );
}
